import { Component } from '@theme/component';
import { WishlistUpdateEvent } from '@theme/events';

const STORAGE_KEY = 'wishlist';

/**
 * Reads the visitor's wishlist (an array of product ids) from localStorage.
 * @returns {string[]}
 */
export function getWishlist() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
}

/**
 * @param {string[]} ids
 */
function setWishlist(ids) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
}

/**
 * A button that adds or removes a product from the visitor's wishlist, persisted in localStorage.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} toggleButton - The clickable button that toggles the wishlist state.
 *
 * @extends {Component<Refs>}
 */
class WishlistButton extends Component {
  requiredRefs = ['toggleButton'];

  connectedCallback() {
    super.connectedCallback();
    this.sync();
  }

  /**
   * Re-applies the `is-active` state from localStorage. The Section Rendering API
   * (e.g. the wishlist grid re-rendering itself) morphs in freshly fetched markup
   * that has no idea which products are wishlisted, so callers that trigger a morph
   * need to call this on every surviving wishlist-button afterwards - otherwise
   * morphing wipes the active state off every button in the section, not just the
   * one that was clicked.
   */
  sync() {
    this.#render(getWishlist().includes(this.#id));
  }

  toggle() {
    if (!this.#id) return;

    const wishlist = getWishlist();
    const index = wishlist.indexOf(this.#id);
    const added = index === -1;

    if (added) {
      wishlist.push(this.#id);
    } else {
      wishlist.splice(index, 1);
    }

    setWishlist(wishlist);
    this.#render(added);

    document.dispatchEvent(new WishlistUpdateEvent(this.#id, added, wishlist));
  }

  get #id() {
    return this.dataset.productId ?? '';
  }

  /**
   * @param {boolean} isActive
   */
  #render(isActive) {
    this.classList.toggle('is-active', isActive);
    this.refs.toggleButton.setAttribute('aria-pressed', String(isActive));
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
